import React, { useState, useEffect } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

interface ImageCarouselModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: any[];
  initialIndex: number;
}

const ImageCarouselModal: React.FC<ImageCarouselModalProps> = ({
  isOpen,
  onClose,
  images,
  initialIndex,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loadingImg, setLoadingImg] = useState(true);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setLoadingImg(true);
    }
  }, [isOpen, initialIndex]);

  if (!isOpen || !images || images.length === 0) return null;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setLoadingImg(true);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setLoadingImg(true);
  };

  const currentItem = images[currentIndex];
  // High-res URL (tempFileUrl for HEIC, local blob url for standard images)
  const highResUrl = currentItem.tempFileUrl || currentItem.url;
  // Low-res URL (thumbnail for HEIC, local blob url for standard images)
  const lowResUrl = currentItem.url || currentItem.tempThumbFileUrl;

  const isLoaded = loadedImages[highResUrl] || false;

  const isValidUrl = (url: any) => typeof url === "string" && (url.startsWith("http") || url.startsWith("data:image/") || url.startsWith("blob:"));

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          width: "100%",
        }}
      >
        <IconButton
          onClick={onClose}
          style={{
            position: "fixed",
            top: 24,
            right: 24,
            color: "#d92b3c",
            backgroundColor: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            zIndex: 9999,
            padding: 8,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {images.length > 1 && (
          <IconButton
            onClick={(e) => { e.preventDefault(); handlePrev(e); }}
            style={{
              position: "fixed",
              left: 24,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 9999,
              backgroundColor: "#fff",
              color: "#d92b3c",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              padding: 10,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        <div style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
          {images.map((item, index) => {
            const hResUrl = item.tempFileUrl || item.url;
            const lResUrl = item.url || item.tempThumbFileUrl;
            const isLoaded = loadedImages[hResUrl] || false;
            const isCurrent = index === currentIndex;

            if (!isValidUrl(hResUrl)) {
              if (!isCurrent) return null;
              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "300px",
                    height: "300px",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    position: "absolute",
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" height="80" width="80" fill="#d92b3c" viewBox="0 0 24 24">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                  </svg>
                </div>
              );
            }

            return (
              <div 
                key={index}
                style={{ 
                  position: "absolute", 
                  top: 0, left: 0, right: 0, bottom: 0, 
                  display: "flex", 
                  justifyContent: "center", 
                  alignItems: "center",
                  opacity: isCurrent ? 1 : 0,
                  visibility: isCurrent ? "visible" : "hidden",
                  transition: "opacity 0.2s ease-in-out",
                  zIndex: isCurrent ? 2 : 1
                }}
              >
                {/* Spinner for the current image if not loaded */}
                {isCurrent && !isLoaded && (
                  <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 5 }}>
                    <svg viewBox="0 0 50 50" style={{ width: "40px", height: "40px", animation: "spin 1s linear infinite", color: "#d92b3c" }}>
                      <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" strokeWidth="4" strokeDasharray="30 100" strokeLinecap="round" />
                      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
                    </svg>
                  </div>
                )}

                {/* Low res blurred image */}
                {!isLoaded && isValidUrl(lResUrl) && (
                  <img
                    src={lResUrl}
                    alt="Loading..."
                    style={{
                      position: "absolute",
                      maxWidth: "100%",
                      maxHeight: "80vh",
                      objectFit: "contain",
                      borderRadius: "8px",
                      filter: "blur(8px)",
                      opacity: 0.6,
                      zIndex: 1,
                    }}
                  />
                )}

                <img
                  src={hResUrl}
                  alt={item.file?.name || "Preview"}
                  onLoad={() => {
                    setLoadedImages(prev => ({ ...prev, [hResUrl]: true }));
                  }}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "80vh",
                    objectFit: "contain",
                    display: "block",
                    borderRadius: "8px",
                    opacity: isLoaded ? 1 : 0,
                    transition: "opacity 0.3s ease-in",
                    position: "relative",
                    zIndex: 2,
                  }}
                />
              </div>
            );
          })}
        </div>

        {images.length > 1 && (
          <IconButton
            onClick={(e) => { e.preventDefault(); handleNext(e); }}
            style={{
              position: "fixed",
              right: 24,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 9999,
              backgroundColor: "#fff",
              color: "#d92b3c",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              padding: 10,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}

        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            color: "#fff",
            fontSize: "0.9rem",
            fontWeight: "bold",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
            backgroundColor: "rgba(0,0,0,0.6)",
            padding: "8px 16px",
            borderRadius: "16px",
            maxWidth: "90%",
            textAlign: "center",
            wordBreak: "break-all",
            zIndex: 9999,
          }}
        >
          <span>{currentItem.file?.name || "Image Preview"}</span>
          <span>{currentIndex + 1} / {images.length}</span>
        </div>
      </div>
    </Dialog>
  );
};

export default ImageCarouselModal;
